export function whatnotShowIdToShowLink(showId: string | undefined): string | undefined {
  if (!showId) return showId;
  return `https://www.whatnot.com/live/${showId}` as any;
}

/**
 * Helper to extract image URL from product media or fallback
 */
export function getItemImageUrl(product: any): string {
  const firstMedia = product.media?.[0];

  if (firstMedia && firstMedia.type === "image" && firstMedia.bucket && firstMedia.key) {
    // Build Whatnot CDN url for correct format/fit/size.
    // Use the largest common variant for best quality in editor context
    return `https://images.whatnot.com/fit-in/256x0/filters:format(webp)/${firstMedia.key}`;
  }

  // fallback to existing url or always-defined string
  return (
    (typeof firstMedia?.url === "string" && firstMedia.url) ||
    "https://images.pexels.com/photos/360624/pexels-photo-360624.jpeg"
  );
}

/**
 * Rewrite a Whatnot CDN image URL to a small unsigned thumbnail variant.
 *
 * DM media URLs arrive as the signed base64-JSON style
 * (`images.whatnot.com/<base64({bucket,key,edits})>?signature=...`) which
 * always serves the full-resolution original — the signature covers the
 * payload, so the size can't be edited in place. The CDN also serves the same
 * keys through the unsigned path-based variant (`/fit-in/WxH/...`), verified
 * against prod keys, so we decode the payload's `key` and rebuild. Any URL we
 * can't confidently rewrite is returned unchanged.
 */
export function toWnThumbnailUrl(url: string, size = 200): string {
  if (!url.startsWith("https://images.whatnot.com/")) return url;

  const path = url.slice("https://images.whatnot.com/".length);
  if (path.startsWith("fit-in/")) return url;

  const payload = path.split("?")[0]!;
  try {
    const decoded = JSON.parse(atob(payload)) as { key?: unknown };
    if (typeof decoded.key !== "string" || !decoded.key) return url;
    return `https://images.whatnot.com/fit-in/${size}x${size}/filters:format(webp)/${decoded.key}`;
  } catch {
    return url;
  }
}

const FALLBACK_AVATAR_URL = "https://images.pexels.com/photos/211816/pexels-photo-211816.jpeg";

/**
 * Build an avatar URL from a Whatnot profileImage key. The CDN's path-based
 * variant (no signature) works for user avatars too — verified against prod
 * keys — so we store just the key and reconstruct here.
 */
export function avatarUrlFromKey(key: string | null | undefined): string {
  if (!key) return FALLBACK_AVATAR_URL;
  return `https://images.whatnot.com/fit-in/256x0/filters:format(webp)/${key}`;
}

/**
 * Build the product CDN URL from a single image_keys[] entry. Mirrors the
 * legacy getItemImageUrl path for callers that only have the key column.
 */
export function imageKeyToUrl(key: string | null | undefined): string {
  if (!key) return "https://images.pexels.com/photos/360624/pexels-photo-360624.jpeg";
  return `https://images.whatnot.com/fit-in/256x0/filters:format(webp)/${key}`;
}
